// ============================================================================
// InstallAppPrompt
// ----------------------------------------------------------------------------
// Field finding (2026-09-22, Android phone, Brave, a real site inspection):
// the app's body never scrolls (components.css `body { overflow: hidden }`),
// so a mobile browser never retracts its URL bar. That bar is the "orange
// bar eating pixels" on a site visit. The only real fix is running as an
// installed PWA (display-mode: standalone), which the manifest already
// declares - but nothing in the app ever invited the operator to install.
// This card does, in the one place it pays off: the Site Visits page, on a
// phone, while NOT already standalone.
//
// Chromium fires `beforeinstallprompt` when the app is installable. The event
// usually arrives before App has mounted (it fires shortly after load, and
// App renders after login), so it is captured at MODULE scope and handed to
// whichever instance mounts later. iOS Safari has no such event, so it gets
// the one-line Share -> Add to Home Screen hint instead.
//
// "Not now" is remembered for 14 days in localStorage. Every read and write
// is wrapped: private mode, blocked site data and quota all throw, and a
// throwing storage must not take the card (or the page) down with it.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Icons } from './Icon';

export const INSTALL_PROMPT_DISMISS_KEY = 'qm_install_prompt_dismissed_until';
export const INSTALL_PROMPT_DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
/** Same phone boundary as BottomNavBar's auto-hide. */
export const INSTALL_PROMPT_PHONE_QUERY = '(max-width: 767px)';
export const INSTALL_PROMPT_PAGE = 'site-visits';

export interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

// ── Module-scope capture of the install event ──────────────────────────────
let capturedPrompt: BeforeInstallPromptEvent | null = null;
const promptSubscribers = new Set<(e: BeforeInstallPromptEvent | null) => void>();

function onBeforeInstallPrompt(e: Event) {
    // Stop Chromium's own mini-infobar; the card owns the moment.
    try { e.preventDefault(); } catch { /* not cancelable in some builds */ }
    capturedPrompt = e as BeforeInstallPromptEvent;
    promptSubscribers.forEach((fn) => fn(capturedPrompt));
}

function onAppInstalled() {
    capturedPrompt = null;
    promptSubscribers.forEach((fn) => fn(null));
}

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
}

/** Test seam: forget a captured event between cases. */
export function __resetCapturedInstallPrompt() {
    capturedPrompt = null;
}

// ── Pure helpers (exported for tests) ──────────────────────────────────────

export function readInstallPromptDismissedUntil(): number {
    try {
        const raw = window.localStorage?.getItem(INSTALL_PROMPT_DISMISS_KEY);
        const n = raw ? Number(raw) : 0;
        return Number.isFinite(n) ? n : 0;
    } catch {
        return 0;
    }
}

export function isInstallPromptDismissed(now: number = Date.now()): boolean {
    return readInstallPromptDismissedUntil() > now;
}

export function rememberInstallPromptDismissed(now: number = Date.now()): void {
    try {
        window.localStorage?.setItem(INSTALL_PROMPT_DISMISS_KEY, String(now + INSTALL_PROMPT_DISMISS_MS));
    } catch {
        /* storage unavailable: the card comes back next load, which is acceptable */
    }
}

export function matchesPhoneViewport(): boolean {
    try {
        return typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia(INSTALL_PROMPT_PHONE_QUERY).matches === true;
    } catch {
        return false;
    }
}

/** True when already running as an installed app (any standalone-ish mode). */
export function isStandaloneDisplay(): boolean {
    try {
        if (typeof window === 'undefined') return false;
        const nav = window.navigator as Navigator & { standalone?: boolean };
        if (nav?.standalone === true) return true; // iOS Safari home-screen flag
        if (typeof window.matchMedia !== 'function') return false;
        return ['standalone', 'window-controls-overlay', 'fullscreen', 'minimal-ui']
            .some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches === true);
    } catch {
        return false;
    }
}

/** iOS (iPhone / iPod / iPad, including iPadOS 13+ reporting a Mac UA). */
export function isIosDevice(ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : ''): boolean {
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    try {
        const nav = typeof navigator !== 'undefined' ? navigator : null;
        return !!nav && /Mac/i.test(nav.platform || '') && (nav.maxTouchPoints || 0) > 1;
    } catch {
        return false;
    }
}

export interface InstallPromptGate {
    currentPage: string | undefined;
    isPhone: boolean;
    standalone: boolean;
    dismissed: boolean;
    /** A captured beforeinstallprompt is available. */
    canPrompt: boolean;
    isIos: boolean;
}

/** The whole decision in one pure function, so a test can enumerate it. */
export function shouldShowInstallPrompt(g: InstallPromptGate): boolean {
    if (g.currentPage !== INSTALL_PROMPT_PAGE) return false;
    if (!g.isPhone) return false;
    if (g.standalone) return false;
    if (g.dismissed) return false;
    return g.canPrompt || g.isIos;
}

// ── Component ──────────────────────────────────────────────────────────────

interface InstallAppPromptProps {
    /** App's routing page id; the card only exists on 'site-visits'. */
    currentPage?: string;
}

export function InstallAppPrompt({ currentPage }: InstallAppPromptProps) {
    const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(() => capturedPrompt);
    const [isPhone, setIsPhone] = useState(() => matchesPhoneViewport());
    const [standalone, setStandalone] = useState(() => isStandaloneDisplay());
    const [dismissed, setDismissed] = useState(() => isInstallPromptDismissed());
    const [ios] = useState(() => isIosDevice());

    // Late-arriving install event, and the installed signal.
    useEffect(() => {
        const sub = (e: BeforeInstallPromptEvent | null) => setPrompt(e);
        promptSubscribers.add(sub);
        if (capturedPrompt) setPrompt(capturedPrompt);
        return () => { promptSubscribers.delete(sub); };
    }, []);

    // Viewport and display-mode changes (rotation, install completing in
    // the same session).
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        let phoneMq: MediaQueryList | null = null;
        let standaloneMq: MediaQueryList | null = null;
        const onPhone = () => setIsPhone(matchesPhoneViewport());
        const onStandalone = () => setStandalone(isStandaloneDisplay());
        try {
            phoneMq = window.matchMedia(INSTALL_PROMPT_PHONE_QUERY);
            standaloneMq = window.matchMedia('(display-mode: standalone)');
            phoneMq.addEventListener?.('change', onPhone);
            standaloneMq.addEventListener?.('change', onStandalone);
        } catch { /* matchMedia stub without listeners */ }
        return () => {
            phoneMq?.removeEventListener?.('change', onPhone);
            standaloneMq?.removeEventListener?.('change', onStandalone);
        };
    }, []);

    const show = shouldShowInstallPrompt({
        currentPage,
        isPhone,
        standalone,
        dismissed,
        canPrompt: !!prompt,
        isIos: ios,
    });
    if (!show) return null;

    const notNow = () => {
        rememberInstallPromptDismissed();
        setDismissed(true);
    };

    const install = async () => {
        const ev = prompt;
        if (!ev) return;
        try {
            await ev.prompt();
            const choice = await ev.userChoice;
            if (choice?.outcome === 'accepted') {
                setStandalone(true); // the card is gone for good; appinstalled follows
            } else {
                notNow(); // a decline in the native sheet is a "not now"
            }
        } catch {
            // prompt() can only be called once per event; the browser refused.
            // Drop the spent event and let the card return on a fresh one.
        }
        setPrompt(null);
    };

    // Flat, one-line, hairline-bordered (Peter, 2026-09-22: no chunky
    // shadows on the phone surface). Buttons keep the 44px phone floor.
    return (
        <div
            role="status"
            data-testid="install-app-prompt"
            style={{
                background: '#fff7f2',
                borderBottom: '1px solid oklch(from var(--qm-accent) 0.8666 calc(c * 0.3369) calc(h + 3.76))',
                padding: '6px 12px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '6px 10px',
                flexShrink: 0,
                zIndex: 41,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 180px', minWidth: 0 }}>
                <Icons.Phone size={16} style={{ color: 'var(--qm-accent-pressed)', flexShrink: 0 }} aria-hidden="true" />
                <span style={{ fontSize: '13px', lineHeight: 1.3, color: 'var(--qm-accent-900)', minWidth: 0 }}>
                    {ios ? (
                        <>
                            <strong>Add QuoteMaker to your Home Screen</strong> for a full-screen site visit:
                            tap Share, then Add to Home Screen.
                        </>
                    ) : (
                        <>
                            <strong>Install QuoteMaker</strong> for a full-screen site visit.
                        </>
                    )}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                {!ios && prompt && (
                    <button
                        type="button"
                        onClick={() => { void install(); }}
                        style={{
                            minHeight: '44px',
                            padding: '0 14px',
                            borderRadius: '6px',
                            border: '1px solid var(--qm-accent-pressed)',
                            background: 'var(--qm-accent)',
                            color: '#1a1a1a',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                    >
                        Install
                    </button>
                )}
                <button
                    type="button"
                    onClick={notNow}
                    aria-label="Not now, ask again in two weeks"
                    style={{
                        minHeight: '44px',
                        padding: '0 12px',
                        borderRadius: '6px',
                        border: '1px solid oklch(from var(--qm-accent) 0.8666 calc(c * 0.3369) calc(h + 3.76))',
                        background: '#fff',
                        color: 'var(--qm-accent-900)',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    Not now
                </button>
            </div>
        </div>
    );
}

export default InstallAppPrompt;
